import { useState, useEffect } from 'react';
import { getMostPopular, createMostPopular, deleteMostPopular } from '@/services/promotionService';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Trash2, Plus } from 'lucide-react';
import { toast } from 'sonner';

export default function MostPopularList() {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [productIdStr, setProductIdStr] = useState('');

  const fetchItems = async () => {
    setLoading(true);
    try {
      const data = await getMostPopular();
      setItems(data);
    } catch (e) {
      toast.error('Failed to load items.');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const handleAdd = async () => {
    const productId = parseInt(productIdStr);
    if (isNaN(productId)) {
      toast.error('Please enter a valid product ID.');
      return;
    }
    try {
      await createMostPopular(productId);
      toast.success('Added successfully.');
      setProductIdStr('');
      fetchItems();
    } catch (e) {
      toast.error('Failed to add. Make sure the product exists.');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteMostPopular(id);
      toast.success('Deleted successfully.');
      fetchItems();
    } catch (e) {
      toast.error('Failed to delete.');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Most Popular</h1>
        <p className="text-muted-foreground">Manage the highlighted most popular products on the frontend.</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Add New Popular Product</CardTitle>
          <CardDescription>Enter a Product ID</CardDescription>
        </CardHeader>
        <CardContent className="flex gap-4">
          <Input 
            placeholder="Product ID (e.g. 12)" 
            value={productIdStr} 
            onChange={(e) => setProductIdStr(e.target.value)} 
          />
          <Button onClick={handleAdd}><Plus className="w-4 h-4 mr-2" /> Add</Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Current Popular</CardTitle>
          <CardDescription>{loading ? 'Loading...' : `Showing ${items.length} items`}</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ID</TableHead>
                <TableHead>Product ID</TableHead>
                <TableHead>Product details</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.id}</TableCell>
                  <TableCell>{item.product_id}</TableCell>
                  <TableCell>
                    {item.products ? (
                      <div className="flex items-center gap-3">
                        {item.products.image && (
                          <img src={item.products.image} alt="pic" className="w-8 h-8 rounded object-cover" />
                        )}
                        <span>{item.products.name}</span>
                      </div>
                    ) : (
                      <span className="text-muted-foreground italic">No data (ID may be invalid)</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDelete(item.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                    No most popular items found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
